import React, { useEffect, useState } from "react";
import {
  Download,
  Loader2,
  Search,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { formatBRL, formatDateTime } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const statusMap = {
  PAID: { label: "PAGO", icon: CheckCircle2, cls: "border-primary/40 text-primary bg-primary/10" },
  ACTIVE: { label: "AGUARDANDO", icon: Clock, cls: "border-amber-500/40 text-amber-400 bg-amber-500/10" },
  EXPIRED: { label: "EXPIRADO", icon: XCircle, cls: "border-muted text-muted-foreground" },
};

export default function Transactions() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "ALL") params.status_filter = statusFilter;
      const res = await api.get("/charges", { params });
      setCharges(res.data);
    } catch {
      toast.error("Erro ao carregar vendas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter]);

  const downloadReceipt = async (charge) => {
    try {
      const token = localStorage.getItem("ml_token");
      const res = await fetch(`${API}/charges/${charge.id}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recibo_${charge.receipt_number || charge.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Recibo baixado");
    } catch (e) {
      toast.error("Erro ao baixar recibo");
    }
  };

  const filtered = charges.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      (c.payer?.name || "").toLowerCase().includes(s) ||
      (c.payer?.cpf || "").includes(s) ||
      (c.customer_name || "").toLowerCase().includes(s) ||
      c.id.toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
          Histórico
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Vendas & Cobranças</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Baixe o recibo em PDF de cada venda paga para se proteger contra contestações PIX MED.
        </p>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-4 md:p-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="search-input"
                placeholder="Buscar por nome, CPF ou ID..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 bg-secondary/40"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48 bg-secondary/40" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos os status</SelectItem>
                <SelectItem value="PAID">Pagos</SelectItem>
                <SelectItem value="ACTIVE">Aguardando</SelectItem>
                <SelectItem value="EXPIRED">Expirados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              Nenhuma transação encontrada.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block rounded-lg border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/30 border-border">
                      <TableHead className="text-xs uppercase tracking-wider">Pagador</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">CPF</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Data/Hora</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Valor</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-right">Recibo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((c) => {
                      const st = statusMap[c.status] || statusMap.ACTIVE;
                      const Icon = st.icon;
                      return (
                        <TableRow key={c.id} className="border-border" data-testid={`tx-row-${c.id}`}>
                          <TableCell className="font-medium">
                            {c.payer?.name || c.customer_name || "—"}
                          </TableCell>
                          <TableCell className="font-mono-num text-muted-foreground">
                            {c.payer?.cpf || "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDateTime(c.paid_at || c.created_at)}
                          </TableCell>
                          <TableCell className="font-mono-num font-semibold">
                            {formatBRL(c.value)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={st.cls}>
                              <Icon className="w-3 h-3 mr-1" />
                              {st.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {c.status === "PAID" ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => downloadReceipt(c)}
                                data-testid={`download-receipt-${c.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {filtered.map((c) => {
                  const st = statusMap[c.status] || statusMap.ACTIVE;
                  const Icon = st.icon;
                  return (
                    <div
                      key={c.id}
                      className="border border-border rounded-lg p-4 bg-secondary/20"
                      data-testid={`tx-mobile-${c.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">
                            {c.payer?.name || c.customer_name || "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {c.payer?.cpf || "—"}
                          </p>
                        </div>
                        <p className="font-mono-num font-semibold text-primary ml-2">
                          {formatBRL(c.value)}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <Badge variant="outline" className={st.cls + " text-[10px]"}>
                            <Icon className="w-3 h-3 mr-1" />
                            {st.label}
                          </Badge>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {formatDateTime(c.paid_at || c.created_at)}
                          </p>
                        </div>
                        {c.status === "PAID" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadReceipt(c)}
                            data-testid={`mobile-download-${c.id}`}
                          >
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            Recibo
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
